'use client'

import { recordAdClick, recordAdImpression } from '@/actions/ads-tracking'
import { hasAdvertisingCookieConsent } from '@/components/cookie-consent-banner'
import { getSessionId } from '@/components/utils/session-tracking'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'

export default function AdCard({ ad, placement }) {
  const trackedImpression = useRef(null)
  const adId = ad?.id
  const cardRef = useRef(null)
  const pathname = usePathname()

  // Require a continuous second at >=50% visibility. Claim the impression
  // synchronously before the request so observer re-entry cannot duplicate it.
  useEffect(() => {
    const element = cardRef.current
    const key = JSON.stringify([adId, pathname, placement])
    if (!adId || !element || trackedImpression.current === key) return
    let timer
    let visible = false
    const cancel = () => {
      clearTimeout(timer)
      timer = undefined
    }
    const schedule = () => {
      cancel()
      if (!visible || document.hidden || trackedImpression.current === key)
        return
      timer = setTimeout(() => {
        trackedImpression.current = key
        recordAdImpression({
          adId,
          page: pathname,
          placement,
          sessionId: hasAdvertisingCookieConsent() ? getSessionId() : null,
        }).catch((error) =>
          console.error('Failed to record ad impression:', error),
        )
      }, 1000)
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        visible = entry.isIntersecting && entry.intersectionRatio >= 0.5
        schedule()
      },
      { threshold: 0.5 },
    )
    observer.observe(element)
    document.addEventListener('visibilitychange', schedule)
    return () => {
      cancel()
      observer.disconnect()
      document.removeEventListener('visibilitychange', schedule)
    }
  }, [adId, placement, pathname])

  const handleCtaClick = () => {
    // Navigate immediately for better UX
    if (ad.targetUrl) {
      window.open(ad.targetUrl, '_blank', 'noopener,noreferrer')
    }

    const consent = hasAdvertisingCookieConsent()
    if (!consent) {
      return
    }

    // Track the click in the background (fire-and-forget)
    const sessionId = getSessionId()

    recordAdClick({
      adId: ad.id,
      fromPage: pathname,
      placement,
      sessionId,
    }).catch((error) => {
      // Log error but don't interrupt user experience
      console.error('Failed to record ad click:', error)
    })
  }

  if (!ad) return null

  return (
    <button
      onClick={handleCtaClick}
      ref={cardRef}
      className="group relative h-full w-full overflow-hidden rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 px-[20px] py-[22px] text-start"
    >
      {/* Card content */}
      <div className="flex h-full flex-col rounded-sm bg-neutral-blue-700">
        {/* Product image */}
        <div className="mt-8 flex w-full justify-center px-4">
          {ad.cardImgUrl && (
            <div className="relative h-[250px] w-full max-w-[300px]">
              <Image
                src={ad.cardImgUrl}
                alt={ad.title || 'Advertisement'}
                fill
                className="object-contain"
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              />
            </div>
          )}
        </div>

        <div className="mb-[18px] mt-12 flex flex-1 flex-col px-6 font-inter">
          {/* Logo */}
          {ad.logoImgUrl && (
            <div className="relative mb-4 h-[32px] w-[186px]">
              <Image
                src={ad.logoImgUrl}
                alt={`${ad.title || 'Advertisement'} logo`}
                fill
                className="object-contain object-left"
                sizes="186px"
              />
            </div>
          )}

          {/* Title and description */}
          <h3 className="text-h-5 font-semibold text-white">{ad.title}</h3>
          {ad.description && (
            <p className="mt-4 text-p-2 text-neutral-blue-200">
              {ad.description}
            </p>
          )}
        </div>

        {/* CTA Button */}
        <div className="flex w-full p-3">
          <div className="flex w-full items-center justify-center gap-2 rounded-sm bg-white px-5 py-4 text-sm font-semibold text-black transition-all duration-200 group-hover:bg-neutral-blue-100 group-hover:shadow-lg">
            <span>{ad.ctaText || 'Redeem Offer'}</span>
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </div>
        </div>
      </div>
    </button>
  )
}
